import { Droplets, Layers, FlaskConical } from 'lucide-react';
import EquipmentAdminValidation from '@/components/EquipmentAdminValidation';

export default function AdminSludgeValidation() {
  return (
    <EquipmentAdminValidation
      title="Sludge Holding / Thickening Validation — Admin Audit"
      description="Vesilind settling, solids flux, polymer dosing, and mass balance. Every equation, every value, every check."
      buildSections={(p) => {
        const flowRate = Number(p.flowRate ?? 25);
        const inletDS = 3.0;
        const targetDS = 6.0;
        const temperature = Number(p.temp ?? 20);
        const polymerDose = 4.0;
        const tankDiameter = 5;
        const tankDepth = 3.5;
        const freeboard = 0.5;
        const workingVolumePercent = 70;
        const fmt = (v: any, d = 2) => (typeof v === 'number' && !isNaN(v) ? v : 0).toFixed(d);

        const q = flowRate / 24;
        const area = Math.PI * Math.pow(tankDiameter / 2, 2);
        const totalVolume = area * (tankDepth + freeboard);
        const workingVolume = totalVolume * (workingVolumePercent / 100);
        const inletDSFraction = inletDS / 100;
        const targetDSFraction = targetDS / 100;
        const drySolidsIn = flowRate * inletDSFraction * 1000;
        const v0 = 5;
        const kThickening = 0.3;
        const inletConcGPerL = inletDSFraction * 1000 * 10;
        const settlingVelocity = v0 * Math.exp(-kThickening * inletConcGPerL / 1000);
        const limitingFlux = inletConcGPerL * settlingVelocity;
        const solidsFlux = (drySolidsIn * 1000) / 24;
        const requiredArea = limitingFlux > 0 ? solidsFlux / limitingFlux : 0;
        const actualFlux = drySolidsIn > 0 ? (drySolidsIn / 24) / area : 0;
        const thickeningRate = ((targetDSFraction - inletDSFraction) / inletDSFraction) * 100;
        const outletFlow = inletDSFraction > 0 ? (flowRate * inletDSFraction) / targetDSFraction : 0;
        const volumeReduction = flowRate > 0 ? ((flowRate - outletFlow) / flowRate) * 100 : 0;
        const supernatantFlow = flowRate - outletFlow;
        const polymerConsumption = (drySolidsIn / 1000) * polymerDose;
        const polymerBoost = polymerDose > 0 ? 1 + (polymerDose / 5) * 0.3 : 1.0;
        const enhancedSettlingVelocity = settlingVelocity * polymerBoost;
        const storageDays = outletFlow > 0 ? workingVolume / outletFlow : 0;
        const detentionTime = workingVolume / q;

        return [
          {
            title: 'Vesilind Settling',
            icon: Droplets,
            equations: [
              { label: 'Inlet concentration', formula: 'C = DS% × 1000 × 10', substitution: `C = ${inletDS}% × 1000 × 10 = ${fmt(inletConcGPerL, 0)} g/L`, result: fmt(inletConcGPerL, 0), status: 'pass', unit: 'g/L' },
              { label: 'Settling velocity', formula: 'v = v₀ × exp(-k × C/1000)', substitution: `v = 5 × exp(-0.3 × ${fmt(inletConcGPerL, 0)}/1000) = ${fmt(settlingVelocity, 2)} m/h`, result: fmt(settlingVelocity, 2), status: settlingVelocity > 1 ? 'pass' : 'warn', unit: 'm/h' },
              { label: 'Limiting flux', formula: 'F = C × v', substitution: `F = ${fmt(inletConcGPerL, 0)} × ${fmt(settlingVelocity, 2)} = ${fmt(limitingFlux, 0)} g/m²·h`, result: fmt(limitingFlux, 0), status: 'pass', unit: 'g/m²·h' },
              { label: 'Required area', formula: 'A = solidsFlux / limitingFlux', substitution: `A = ${fmt(solidsFlux, 0)} / ${fmt(limitingFlux, 0)} = ${fmt(requiredArea, 1)} m²`, result: fmt(requiredArea, 1), status: requiredArea <= area ? 'pass' : 'warn', unit: 'm²' },
            ],
          },
          {
            title: 'Solids Flux & Thickening',
            icon: Layers,
            equations: [
              { label: 'Tank area', formula: 'A = π × (D/2)²', substitution: `A = π × (${tankDiameter}/2)² = ${fmt(area, 1)} m²`, result: fmt(area, 1), status: 'pass', unit: 'm²' },
              { label: 'Actual flux', formula: 'F = (DS_in / 24) / A', substitution: `F = (${fmt(drySolidsIn, 0)}/24) / ${fmt(area, 1)} = ${fmt(actualFlux, 1)} kg/m²·h`, result: fmt(actualFlux, 1), status: actualFlux <= 20 ? 'pass' : actualFlux <= 50 ? 'warn' : 'fail', unit: 'kg/m²·h' },
              { label: 'Thickening rate', formula: 'θ = (DS_out - DS_in) / DS_in × 100', substitution: `θ = (${targetDS} - ${inletDS}) / ${inletDS} × 100 = ${fmt(thickeningRate, 0)}%`, result: fmt(thickeningRate, 0), status: thickeningRate >= 30 ? 'pass' : 'warn', unit: '%' },
              { label: 'Volume reduction', formula: 'R = (Q_in - Q_out) / Q_in × 100', substitution: `R = (${fmt(flowRate, 0)} - ${fmt(outletFlow, 1)}) / ${fmt(flowRate, 0)} × 100 = ${fmt(volumeReduction, 0)}%`, result: fmt(volumeReduction, 0), status: volumeReduction >= 25 ? 'pass' : 'warn', unit: '%' },
              { label: 'Supernatant flow', formula: 'Q_s = Q_in - Q_out', substitution: `Q_s = ${fmt(flowRate, 0)} - ${fmt(outletFlow, 1)} = ${fmt(supernatantFlow, 1)} m³/d`, result: fmt(supernatantFlow, 1), status: 'pass', unit: 'm³/d' },
            ],
          },
          {
            title: 'Polymer Dosing & Storage',
            icon: FlaskConical,
            equations: [
              { label: 'Polymer consumption', formula: 'm_p = (DS_tonnes/d) × dose', substitution: `m_p = (${fmt(drySolidsIn / 1000, 2)}) × ${polymerDose} = ${fmt(polymerConsumption, 1)} kg/d`, result: fmt(polymerConsumption, 1), status: polymerConsumption <= 50 ? 'pass' : 'warn', unit: 'kg/d' },
              { label: 'Polymer boost', formula: 'Boost = 1 + (dose/5) × 0.3', substitution: `Boost = 1 + (${polymerDose}/5) × 0.3 = ${fmt(polymerBoost, 2)}×`, result: fmt(polymerBoost, 2), status: 'pass', unit: '' },
              { label: 'Enhanced settling', formula: 'v_enh = v × boost', substitution: `v_enh = ${fmt(settlingVelocity, 2)} × ${fmt(polymerBoost, 2)} = ${fmt(enhancedSettlingVelocity, 2)} m/h`, result: fmt(enhancedSettlingVelocity, 2), status: 'pass', unit: 'm/h' },
              { label: 'Storage days', formula: 'S = V_working / Q_out', substitution: `S = ${fmt(workingVolume, 1)} / ${fmt(outletFlow, 1)} = ${fmt(storageDays, 1)} d`, result: fmt(storageDays, 1), status: storageDays >= 1 ? 'pass' : storageDays >= 0.5 ? 'warn' : 'fail', unit: 'days' },
              { label: 'Detention time', formula: 'τ = V_working / Q', substitution: `τ = ${fmt(workingVolume, 1)} / ${fmt(q, 2)} = ${fmt(detentionTime, 1)} h`, result: fmt(detentionTime, 1), status: detentionTime <= 24 ? 'pass' : 'warn', unit: 'h' },
            ],
          },
        ];
      }}
    />
  );
}
